import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react"

import {
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  getWeek,
  isBefore,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
} from "date-fns"
import { AlertCircle, CheckCircle } from "lucide-react"

import { Spinner } from "@/components/ui/spinner"
import { useMonthlyShiftStats } from "@/hooks/useMonthlyShiftStats"
import { ukDayEndOf, ukDayStartOf } from "@/lib/ukCalendarDate"
import { exportMonthlyStatsToCSV } from "@/utils/csvExportUtils"

import type { Enums } from "../../../../database.types"
import { MonthlyScheduleSkeleton } from "./SkeletonLoaders"

export interface MonthlyScheduleViewRef {
  exportToCSV: () => void
}

interface MonthlyScheduleViewProps {
  date: Date
  scheduleType: "delivery" | "collection" | "sortation" | "internal"
  filters: {
    location: string
    employer: string
    type: string
    areas: string[]
  }
  onDateClick?: (date: Date) => void
  onLoadingChange?: (isLoading: boolean) => void
}

const MonthlyScheduleView = forwardRef<MonthlyScheduleViewRef, MonthlyScheduleViewProps>(
  ({ date, scheduleType: _scheduleType, filters, onDateClick, onLoadingChange }, ref) => {
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

    const monthStart = startOfMonth(date)
    const monthEnd = endOfMonth(date)
    // The month runs from the first instant of its first UK day to the last of its last
    const monthStartTime = ukDayStartOf(monthStart)
    const monthEndTime = ukDayEndOf(monthEnd)
    const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd })

    // Fetch aggregated stats for the entire month (much faster than fetching all shifts)
    const {
      data: dayStats = [],
      isLoading,
      isFetching,
    } = useMonthlyShiftStats({
      startDate: monthStartTime.toISOString(),
      endDate: monthEndTime.toISOString(),
      location: filters.location || undefined,
      employer: filters.employer !== "all" ? filters.employer : undefined,
      type: filters.type !== "all" ? (filters.type as Enums<"person_type">) : undefined,
      areas:
        filters.areas && filters.areas.length
          ? (filters.areas as Enums<"person_areas">[])
          : undefined,
    })

    // Track if we've loaded at least once
    useEffect(() => {
      if (!isLoading && dayStats.length >= 0) {
        setHasLoadedOnce(true)
      }
    }, [isLoading, dayStats.length])

    // Notify parent about loading state
    useEffect(() => {
      onLoadingChange?.(isFetching && hasLoadedOnce)
    }, [isFetching, hasLoadedOnce, onLoadingChange])

    // Create a map for quick lookup
    const statsByDate = useMemo(() => {
      const map = new Map<string, (typeof dayStats)[0]>()
      dayStats.forEach((stat) => {
        map.set(stat.date, stat)
      })
      return map
    }, [dayStats])

    const getDaySummary = (day: Date) => {
      const dateStr = format(day, "yyyy-MM-dd")
      const stats = statsByDate.get(dateStr)

      const totalBlocks = stats?.total || 0
      const planned = stats?.filled || 0
      const unfulfilled = stats?.unfulfilled || 0

      let status = "no-action"
      let statusText = "No Action Required"
      let statusIcon = <CheckCircle className="h-4 w-4 text-green-600" />

      if (unfulfilled > 0) {
        status = "empty-slot"
        statusText = `${unfulfilled} Unfulfilled Block${unfulfilled !== 1 ? "s" : ""}`
        statusIcon = <AlertCircle className="h-4 w-4 text-red-600" />
      }

      return {
        totalBlocks,
        planned,
        status,
        statusText,
        statusIcon,
      }
    }

    const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]

    // Group days by weeks, accounting for the starting day of the month
    const weeks = useMemo(() => {
      const firstDayOfMonth = getDay(monthStart)
      // Convert Sunday (0) to 6, and shift Monday (1) to 0
      const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1

      const weeksArray: (Date | null)[][] = []
      let currentWeek: (Date | null)[] = []

      // Add empty cells for days before the 1st
      for (let i = 0; i < startOffset; i++) {
        currentWeek.push(null)
      }

      // Add all days of the month
      monthDays.forEach((day) => {
        currentWeek.push(day)
        if (currentWeek.length === 7) {
          weeksArray.push(currentWeek)
          currentWeek = []
        }
      })

      // Add remaining cells to complete the last week
      if (currentWeek.length > 0) {
        while (currentWeek.length < 7) {
          currentWeek.push(null)
        }
        weeksArray.push(currentWeek)
      }

      return weeksArray
    }, [monthDays, monthStart])

    const handleExportCSV = useCallback(() => {
      const filename = `shifts_monthly_${format(date, "yyyy-MM")}_${dayStats.length}.csv`
      exportMonthlyStatsToCSV(dayStats, filename)
    }, [date, dayStats])

    useImperativeHandle(
      ref,
      () => ({
        exportToCSV: handleExportCSV,
      }),
      [handleExportCSV],
    )

    // Show skeleton loader on initial load
    if (isLoading && !hasLoadedOnce) {
      return <MonthlyScheduleSkeleton />
    }

    return (
      <div className="space-y-4">
        {/* Show updating indicator when refetching */}
        {isFetching && hasLoadedOnce && (
          <div className="flex items-center justify-center gap-2 text-sm text-palette-jungleGreen600 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
            <Spinner size="sm" />
            <span>Updating calendar...</span>
          </div>
        )}

        {/* Monthly Schedule Grid */}
        <div className="bg-white border border-palette-jungleGreen200 rounded-xl overflow-auto max-h-[calc(100vh-200px)] shadow-lg">
          {/* Header Row - Sticky */}
          <div className="sticky top-0 z-10 grid grid-cols-8 bg-palette-jungleGreen800 text-white border-b-2 border-palette-jungleGreen600 shadow-xl">
            <div className="p-3 text-center font-semibold border-r border-palette-jungleGreen600">
              Week
            </div>
            {weekDays.map((day) => (
              <div
                key={day}
                className="p-3 text-center font-semibold border-r border-palette-jungleGreen600 last:border-r-0"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Weekly Rows */}
          {weeks.map((week, weekIndex) => {
            // Get the actual ISO week number from the first non-null day in the week
            const firstDayInWeek = week.find((day) => day !== null)
            const weekNumber = firstDayInWeek
              ? getWeek(firstDayInWeek, { weekStartsOn: 1 })
              : weekIndex + 1

            return (
              <div key={weekIndex} className="border-b last:border-b-0 relative">
                {/* Week Header */}
                <div className="grid grid-cols-8 bg-palette-jungleGreen100 border-b-2 border-palette-jungleGreen200">
                  <div className="sticky left-0 z-[5] p-3 text-center font-semibold text-palette-jungleGreen700 border-r border-palette-jungleGreen300 bg-palette-jungleGreen100">
                    Week {weekNumber}
                  </div>
                  {week.map((day, dayIndex) => {
                    const isTodayDate = day && isToday(day)
                    const isPast = day && isBefore(startOfDay(day), startOfDay(new Date()))
                    const isFuture = day && !isTodayDate && !isPast
                    return (
                      <div
                        key={dayIndex}
                        className={`p-3 text-center font-semibold border-r border-palette-jungleGreen300 last:border-r-0 transition-all ${
                          isTodayDate
                            ? "bg-blue-500 text-white font-bold shadow-lg ring-2 ring-blue-300 ring-inset"
                            : isPast
                              ? "bg-palette-jungleGreen300 text-palette-jungleGreen500"
                              : isFuture
                                ? "bg-blue-50 text-blue-800"
                                : "bg-palette-jungleGreen100 text-palette-jungleGreen700"
                        }`}
                      >
                        {day ? format(day, "do") : ""}
                      </div>
                    )
                  })}
                </div>

                {/* Planned Row */}
                <div className="grid grid-cols-8 border-b hover:bg-palette-jungleGreen000/50 transition-colors">
                  <div className="sticky left-0 z-[5] p-2 text-sm font-semibold text-palette-jungleGreen700 bg-palette-jungleGreen000 border-r border-palette-jungleGreen200 flex items-center justify-center">
                    Planned
                  </div>
                  {week.map((day, dayIndex) => {
                    if (!day) {
                      return (
                        <div
                          key={dayIndex}
                          className="p-2 text-center border-r last:border-r-0 bg-palette-jungleGreen000"
                        >
                          <span className="text-sm text-palette-jungleGreen400">-</span>
                        </div>
                      )
                    }

                    const summary = getDaySummary(day)
                    const isCurrentMonth = isSameMonth(day, date)
                    const isTodayDate = isToday(day)
                    const isPast = isBefore(startOfDay(day), startOfDay(new Date()))

                    return (
                      <div
                        key={dayIndex}
                        className={`p-2 text-center border-r border-palette-jungleGreen200 last:border-r-0 ${
                          isTodayDate ? "bg-blue-50" : isPast ? "bg-palette-jungleGreen100" : ""
                        }`}
                      >
                        {isCurrentMonth ? (
                          <span
                            className={`text-sm font-bold ${isPast ? "text-palette-jungleGreen400" : "text-palette-jungleGreen900"}`}
                          >
                            {summary.totalBlocks}
                          </span>
                        ) : (
                          <span className="text-sm text-palette-jungleGreen400">-</span>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* Rostered Row */}
                <div className="grid grid-cols-8 border-b hover:bg-palette-jungleGreen000/50 transition-colors">
                  <div className="sticky left-0 z-[5] p-2 text-sm font-semibold text-palette-jungleGreen700 bg-palette-jungleGreen000 border-r border-palette-jungleGreen200 flex items-center justify-center">
                    Scheduled
                  </div>
                  {week.map((day, dayIndex) => {
                    if (!day) {
                      return (
                        <div
                          key={dayIndex}
                          className="p-2 text-center border-r last:border-r-0 bg-palette-jungleGreen000"
                        >
                          <span className="text-sm text-palette-jungleGreen400">-</span>
                        </div>
                      )
                    }

                    const summary = getDaySummary(day)
                    const isCurrentMonth = isSameMonth(day, date)
                    const isTodayDate = isToday(day)
                    const isPast = isBefore(startOfDay(day), startOfDay(new Date()))

                    return (
                      <div
                        key={dayIndex}
                        className={`p-2 text-center border-r border-palette-jungleGreen200 last:border-r-0 ${
                          isTodayDate ? "bg-blue-50" : isPast ? "bg-palette-jungleGreen100" : ""
                        }`}
                      >
                        {isCurrentMonth ? (
                          <span
                            className={`text-sm font-bold ${isPast ? "text-palette-jungleGreen400" : "text-palette-jungleGreen900"}`}
                          >
                            {summary.planned}
                          </span>
                        ) : (
                          <span className="text-sm text-palette-jungleGreen400">-</span>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* Status Row */}
                <div className="grid grid-cols-8 border-b-2 border-palette-jungleGreen200">
                  <div className="sticky left-0 z-[5] p-2 text-sm font-semibold text-palette-jungleGreen700 bg-palette-jungleGreen000 border-r border-palette-jungleGreen200 flex items-center justify-center">
                    Status
                  </div>
                  {week.map((day, dayIndex) => {
                    if (!day) {
                      return (
                        <div
                          key={dayIndex}
                          className="p-2 text-center border-r last:border-r-0 bg-palette-jungleGreen000"
                        >
                          <span className="text-sm text-palette-jungleGreen400">-</span>
                        </div>
                      )
                    }

                    const summary = getDaySummary(day)
                    const isCurrentMonth = isSameMonth(day, date)
                    const isTodayDate = isToday(day)
                    const isPast = isBefore(startOfDay(day), startOfDay(new Date()))

                    // Determine background color based on status
                    let cellBgColor = ""
                    let textColor = "text-palette-jungleGreen700"
                    let statusText = summary.statusText
                    let statusIcon = summary.statusIcon

                    if (isPast && isCurrentMonth) {
                      // Past days show as completed
                      cellBgColor = "bg-palette-jungleGreen100"
                      textColor = "text-palette-jungleGreen500"
                      statusText = "Completed"
                      statusIcon = <CheckCircle className="h-4 w-4 text-palette-jungleGreen500" />
                    } else if (isCurrentMonth && summary.totalBlocks > 0) {
                      if (summary.status === "empty-slot") {
                        cellBgColor = "bg-red-100"
                        textColor = "text-red-800"
                      } else if (summary.status === "no-action") {
                        cellBgColor = "bg-green-50"
                        textColor = "text-green-700"
                      } else if (summary.status === "over-capacity") {
                        cellBgColor = "bg-orange-100"
                        textColor = "text-orange-800"
                      }
                    } else if (isTodayDate) {
                      cellBgColor = "bg-blue-50"
                    }

                    return (
                      <div
                        key={dayIndex}
                        className={`p-2 text-center border-r border-palette-jungleGreen200 last:border-r-0 ${cellBgColor} transition-colors flex items-center justify-center min-h-[40px]`}
                      >
                        {isCurrentMonth && summary.totalBlocks > 0 ? (
                          <div className="flex items-center justify-center gap-1.5">
                            {statusIcon}
                            <span className={`text-xs font-semibold ${textColor}`}>
                              {statusText}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-palette-jungleGreen400">-</span>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* Clickable Day Columns */}
                {week.map((day, dayIndex) => {
                  if (!day) {
                    return (
                      <div
                        key={`clickable-${dayIndex}`}
                        className={`absolute top-0 bottom-0 w-[calc(100%/8)] border-r last:border-r-0 ${
                          dayIndex === 0
                            ? "left-[12.5%]"
                            : dayIndex === 1
                              ? "left-[25%]"
                              : dayIndex === 2
                                ? "left-[37.5%]"
                                : dayIndex === 3
                                  ? "left-[50%]"
                                  : dayIndex === 4
                                    ? "left-[62.5%]"
                                    : dayIndex === 5
                                      ? "left-[75%]"
                                      : "left-[87.5%]"
                        }`}
                      />
                    )
                  }

                  const isCurrentMonth = isSameMonth(day, date)
                  const isPast = isBefore(startOfDay(day), startOfDay(new Date()))
                  const isClickable = isCurrentMonth && !isPast

                  return (
                    <div
                      key={`clickable-${dayIndex}`}
                      className={`absolute top-0 bottom-0 w-[calc(100%/8)] border-r last:border-r-0 ${
                        dayIndex === 0
                          ? "left-[12.5%]"
                          : dayIndex === 1
                            ? "left-[25%]"
                            : dayIndex === 2
                              ? "left-[37.5%]"
                              : dayIndex === 3
                                ? "left-[50%]"
                                : dayIndex === 4
                                  ? "left-[62.5%]"
                                  : dayIndex === 5
                                    ? "left-[75%]"
                                    : "left-[87.5%]"
                      } ${isClickable ? "cursor-pointer hover:bg-palette-jungleGreen100 hover:bg-opacity-30" : isPast ? "cursor-not-allowed" : ""}`}
                      onClick={() => isClickable && onDateClick?.(day)}
                    />
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    )
  },
)

export default MonthlyScheduleView
